import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { parseApiError } from "../../lib/errors";
import { Field, Input, MultiSelect, Modal, PrimaryButton, QuietButton } from "../UI";

const EMPTY = { company_name: "", contact_name: "", contact_email: "", contact_phone: "", trade_ids: [], notes: "" };

/** LT-37 — add / edit a directory subcontractor. Trades are Labour Rates rows (picked by name). */
export function SubcontractorDialog({ open, onClose, initial, trades, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(initial ? {
      company_name: initial.company_name || "", contact_name: initial.contact_name || "",
      contact_email: initial.contact_email || "", contact_phone: initial.contact_phone || "",
      trade_ids: initial.trade_ids || [], notes: initial.notes || "",
    } : EMPTY);
  }, [open, initial]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const nameById = Object.fromEntries((trades || []).map((t) => [t.id, t.trade_name]));
  const idByName = Object.fromEntries((trades || []).map((t) => [t.trade_name, t.id]));

  const save = async () => {
    if (!form.company_name.trim()) { setError({ message: "Company name is required." }); return; }
    setSaving(true); setError(null);
    try {
      const r = initial
        ? await api.patch(`/subcontractors/${initial.id}`, form)
        : await api.post("/subcontractors", form);
      toast.success(initial ? "Subcontractor updated" : "Subcontractor added");
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      setError(parseApiError(e, "Could not save the subcontractor."));
    } finally { setSaving(false); }
  };

  const restore = async () => {
    try {
      await api.post(`/subcontractors/${error.existing_id}/activate`);
      toast.success(`${error.existing_label} restored`);
      onSaved?.(); onClose();
    } catch (e) { setError(parseApiError(e, "Restore failed.")); }
  };

  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit subcontractor" : "Add subcontractor"} testid="subby-dialog"
      footer={<>
        <QuietButton onClick={onClose} data-testid="subby-dialog-cancel">Cancel</QuietButton>
        <PrimaryButton onClick={save} disabled={saving} icon={saving ? Loader2 : undefined} data-testid="subby-dialog-save">
          {initial ? "Save" : "Add subcontractor"}
        </PrimaryButton>
      </>}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Field label="Company" required>
            <Input value={form.company_name} onChange={set("company_name")} placeholder="e.g. Smith Scaffolding Ltd" data-testid="subby-dialog-company" />
          </Field>
        </div>
        <Field label="Contact name">
          <Input value={form.contact_name} onChange={set("contact_name")} data-testid="subby-dialog-contact-name" />
        </Field>
        <Field label="Phone">
          <Input value={form.contact_phone} onChange={set("contact_phone")} data-testid="subby-dialog-contact-phone" />
        </Field>
        <div className="col-span-2">
          <Field label="Email">
            <Input value={form.contact_email} onChange={set("contact_email")} type="email" data-testid="subby-dialog-contact-email" />
          </Field>
        </div>
        <div className="col-span-2" data-testid="subby-dialog-trades">
          <Field label="Trades" hint="From Labour Rates — add the trade there first if it is missing.">
            <MultiSelect
              value={form.trade_ids.map((id) => nameById[id]).filter(Boolean)}
              onChange={(names) => set("trade_ids")(names.map((n) => idByName[n]).filter(Boolean))}
              options={(trades || []).map((t) => t.trade_name)}
              placeholder="Add a trade…"
            />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={3} data-testid="subby-dialog-notes"
              className="w-full px-3 py-2 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-y"
              style={{ fontSize: 13, lineHeight: "18px" }} />
          </Field>
        </div>
      </div>
      {error && (
        <div className="mt-4 rounded-md border px-3 py-2" style={{ background: "#FBECEC", borderColor: "#E9C0C0", color: "#7A1F1F", fontSize: 12 }} data-testid="subby-dialog-error">
          {error.message}
          {error.existing_status === "archived" && error.existing_id && (
            <button type="button" onClick={restore} className="ml-2 underline" data-testid="subby-dialog-restore">Restore it</button>
          )}
        </div>
      )}
    </Modal>
  );
}
